/* <gr-sticky-atc> — the PDP sticky buy bar.

   Three jobs, and deliberately nothing else:

   1. Reveal itself once #gr-sentinel (rendered at the foot of the buy box by
      sections/main-product.liquid) has scrolled past the top of the viewport.
      An IntersectionObserver, not a scroll listener and not a timer.
   2. Publish its own measured height into --gr-sticky-atc-height so the footer
      can reserve exactly the right space, with no page-specific magic number.
   3. Return the shopper to the bundle selector, where quantity and purchase
      options can be reviewed before the real add-to-cart action.

   It has no pricing logic: every money value in the bar carries
   [data-gr-price] and is painted by gr-variant-state, the single source of
   truth for the selected variant and selling plan. */

class GrStickyAtc extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.button = this.querySelector('[data-sticky-add]');
    this.bottleTile = this.querySelector('[data-sticky-tile]');
    this.bottleValue = this.querySelector('[data-sticky-bottles]');

    this.onClick = this.onClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onIntersect = this.onIntersect.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);

    if (this.button) this.button.addEventListener('click', this.onClick);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // A deferred script can run before this section's stylesheet has applied,
    // so the first measurement is confirmed once everything has loaded.
    window.addEventListener('load', this.onResize);
    document.addEventListener('gr:variant:change', this.onVariantChange);
    // The buy box is its own section: in the theme editor it can re-render
    // underneath us, which replaces the sentinel node and the product form.
    document.addEventListener('shopify:section:load', this.onSectionLoad);

    // Content reflow (a longer price, a wrapped title) changes the height the
    // footer has to reserve just as much as a viewport change does.
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
    }

    this.observeSentinel();
    this.publishHeight();
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('load', this.onResize);
    document.removeEventListener('gr:variant:change', this.onVariantChange);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);

    if (this.observer) { this.observer.disconnect(); this.observer = null; }
    if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null; }
    if (this.frame) { window.cancelAnimationFrame(this.frame); this.frame = null; }
    if (this.scrollFrame) { window.cancelAnimationFrame(this.scrollFrame); this.scrollFrame = null; }

    // The bar is leaving the document, so the footer must stop reserving space
    // for it. A re-render immediately re-publishes the real height.
    document.documentElement.style.setProperty('--gr-sticky-atc-height', '0px');

    this._bound = false;
  }

  get disabled() { return this.hasAttribute('data-disabled'); }

  /* ---------------- Reveal ---------------- */

  observeSentinel() {
    if (this.observer) { this.observer.disconnect(); this.observer = null; }

    const id = this.getAttribute('data-sentinel');
    this.sentinel = id ? document.getElementById(id) : null;
    // No buy box on this page means nothing to be sticky about: the bar stays
    // in its hidden state rather than floating over unrelated content.
    if (this.disabled || !this.sentinel) {
      this.classList.remove('is-on');
      return;
    }

    if (window.IntersectionObserver) {
      this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0 });
      this.observer.observe(this.sentinel);
    }
    this.syncPassedState();
  }

  onIntersect() {
    this.syncPassedState();
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.syncPassedState();
    });
  }

  syncPassedState() {
    if (this.disabled || !this.sentinel) {
      this.classList.remove('is-on');
      return;
    }
    this.classList.toggle('is-on', this.sentinel.getBoundingClientRect().top < 0);
  }

  onSectionLoad() {
    // Re-point the observer at whatever sentinel the editor rendered.
    this.observeSentinel();
    this.publishHeight();
  }

  /* ---------------- Height ---------------- */

  onResize() { this.publishHeight(); }

  publishHeight() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      // offsetHeight is the bar's full border box including the safe-area
      // padding, and a transform does not change it — so the value stays
      // correct whether the bar is currently revealed or parked off-screen.
      // When the section is switched off the element is display:none and this
      // is 0, which is exactly what the footer should then reserve.
      document.documentElement.style.setProperty('--gr-sticky-atc-height', this.offsetHeight + 'px');
    });
  }

  /* ---------------- Variant ---------------- */

  onVariantChange(event) {
    const detail = event.detail || {};
    const variant = detail.variant;
    const count = variant && variant.bottle_count ? variant.bottle_count : 0;

    if (this.bottleValue) this.bottleValue.textContent = count > 0 ? String(count) : '';
    if (this.bottleTile) this.bottleTile.hidden = count <= 0;

    // The prices in the bar were just repainted by gr-variant-state; a longer
    // or shorter string can change the bar's height.
    this.publishHeight();
  }

  /* ---------------- Return to bundle selector ---------------- */

  bundleTarget() {
    return document.querySelector('kaching-bundles-block .kaching-bundles__block') ||
      document.querySelector('kaching-bundles-block') ||
      document.querySelector('.gr-buy__tiers') ||
      document.getElementById('buy') ||
      document.querySelector('.gr-pdp__buy');
  }

  onClick(event) {
    event.preventDefault();

    const target = this.bundleTarget();
    if (!target) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: reduced ? 'auto' : 'smooth',
      block: 'center'
    });

    const selected = target.querySelector('input[type="radio"]:checked');
    if (selected) {
      window.setTimeout(() => selected.focus({ preventScroll: true }), reduced ? 0 : 450);
    }
  }
}

if (!customElements.get('gr-sticky-atc')) customElements.define('gr-sticky-atc', GrStickyAtc);
